// /slide-preview — minimal slide renderer used by the mobile remote.
//
// We render the SAME `<LessonStage>` component the student smartboard uses,
// fed a synthetic state with all fragments revealed (`slideRenderMode: "full"`)
// and chrome disabled. This guarantees pixel-fidelity with the smartboard
// (images, layouts, activities, charts, KaTeX — everything looks identical,
// just shrunk to 16:9).
//
// URL: /slide-preview?lessonId=quadratic-equations&slideId=shape-of-x2
//
// ─────────────── Live slide switching ───────────────
// The page mounts ONCE per pairing and stays in the WebView for the whole
// session. The host (React Native WebView) drives slide switches by
// injecting:
//     window.dispatchEvent(new MessageEvent('message', {
//         data: JSON.stringify({type:'sparkl:set-slide', slideId:'…'})
//     }));
// This avoids the ~2s WebView reload that used to flash a black frame on
// every slide change. We also preload every slide's images on first mount
// so subsequent switches paint instantly.
//
// The Gaseous deck stays handled separately because it's an external HTML
// deck (not a React slide).

import React, { useEffect, useMemo, useState } from "react";
import { LessonStage } from "../components/LessonStage";
import {
    lesson,
    setActiveLessonId,
    findSlideIdxById,
} from "../lib/lessonData";
import { gaseousLesson } from "../lib/gaseousLesson";

const SlidePreview = () => {
    const params = new URLSearchParams(window.location.search);
    const lessonId = params.get("lessonId") || "photosynthesis";
    const initialSlideId = params.get("slideId");
    const initialActiveMcqId = params.get("activeMcqId");
    const initialRevealMcqAnswer = params.get("revealMcqAnswer") === "true";
    const initialActiveExampleId = params.get("activeExampleId");

    // Resolve active lesson before reading slides.
    setActiveLessonId(lessonId);

    const isGaseous = lessonId === "gaseous-state";

    // Slide id is held in state so the host can swap it via postMessage
    // without us having to reload the page.
    const [slideId, setSlideId] = useState(initialSlideId);
    // Photo subslide override — when the host injects a photo data URL
    // we render a blank slide with the photo as content (matches the
    // smartboard) instead of the regular slide.
    const [photoOverride, setPhotoOverride] = useState(null);
    // State overrides for MCQ and other dynamic slides
    const [stateOverrides, setStateOverrides] = useState({
        activeMcqId: initialActiveMcqId,
        revealMcqAnswer: initialRevealMcqAnswer,
        activeExampleId: initialActiveExampleId,
    });

    // Reset background / scroll for this preview window only, AND hide the
    // Emergent platform badge — it overlaps the 16:9 mobile WebView and
    // is irrelevant inside the in-app preview. The badge has inline
    // `display: inline-flex !important;` which beats stylesheet rules on
    // specificity, so we override its own inline style directly.
    useEffect(() => {
        const prevBg = document.body.style.background;
        const prevOverflow = document.body.style.overflow;
        document.body.style.background = "#ffffff";
        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        const hideBadge = () => {
            const badge = document.getElementById("emergent-badge");
            if (badge)
                badge.style.setProperty("display", "none", "important");
        };
        hideBadge();
        const interval = setInterval(hideBadge, 250);
        const stop = setTimeout(() => clearInterval(interval), 3000);

        return () => {
            clearInterval(interval);
            clearTimeout(stop);
            document.body.style.background = prevBg;
            document.body.style.overflow = prevOverflow;
            document.documentElement.style.overflow = "";
        };
    }, []);

    // Listen for slide-switch messages from the React Native host.
    // We accept BOTH patterns so the host can use whichever is convenient:
    //   1. `window.sparklSetSlide(id)` — direct call (preferred, simplest)
    //   2. `window.postMessage({type:'sparkl:set-slide', slideId:id}, '*')`
    useEffect(() => {
        const onMsg = (e) => {
            try {
                const raw = e?.data;
                const data =
                    typeof raw === "string" ? JSON.parse(raw) : raw;
                if (data?.type === "sparkl:set-slide" && data.slideId) {
                    setPhotoOverride(null);
                    setStateOverrides(data.overrides || {});
                    setSlideId(data.slideId);
                } else if (
                    data?.type === "sparkl:set-photo-slide" &&
                    data.photo
                ) {
                    setPhotoOverride(data.photo);
                }
            } catch {
                /* malformed payload — ignore */
            }
        };
        window.addEventListener("message", onMsg);
        // Expose a tiny imperative bridge so the React Native host can
        // synchronously trigger a slide switch via `injectJavaScript`.
        window.sparklSetSlide = (id, overrides) => {
            if (id && typeof id === "string") {
                setPhotoOverride(null);
                setStateOverrides(overrides || {});
                setSlideId(id);
            }
        };
        window.sparklSetPhotoSlide = (photo) => {
            if (photo && typeof photo === "string") setPhotoOverride(photo);
        };
        return () => {
            window.removeEventListener("message", onMsg);
            try {
                delete window.sparklSetSlide;
                delete window.sparklSetPhotoSlide;
            } catch {
                /* noop */
            }
        };
    }, []);

    // Preload every slide's image / poster assets so subsequent switches
    // paint without flicker. We scrape http(s) image URLs out of the
    // lesson JSON — cheap, defensive, no schema coupling.
    useEffect(() => {
        if (isGaseous) return;
        const collect = () => {
            const urls = new Set();
            try {
                const json = JSON.stringify(lesson);
                const re =
                    /https?:\/\/[^\s"'<>]+\.(?:jpg|jpeg|png|webp|gif|svg)(?:\?[^\s"'<>]*)?/gi;
                let m;
                while ((m = re.exec(json))) urls.add(m[0]);
            } catch {
                /* noop */
            }
            return urls;
        };
        const urls = collect();
        const imgs = [];
        urls.forEach((u) => {
            const img = new Image();
            img.decoding = "async";
            img.loading = "eager";
            img.src = u;
            imgs.push(img);
        });
        // Tell the React Native host how many we kicked off — useful for
        // debugging on the phone (visible via WebView console).
        try {
            // eslint-disable-next-line no-console
            console.log(`[sparkl] preloaded ${imgs.length} slide images`);
            window.ReactNativeWebView?.postMessage?.(
                JSON.stringify({
                    type: "sparkl:assets-preloaded",
                    count: imgs.length,
                }),
            );
        } catch {
            /* noop */
        }
        // hold references on window so GC doesn't drop them mid-load
        try {
            window.__sparklPreloadedImgs = imgs;
        } catch {
            /* noop */
        }
    }, [isGaseous, lessonId]);

    // Synthetic "fully revealed" state for the picked slide.
    const previewState = useMemo(() => {
        if (isGaseous) return null;
        const idx = slideId
            ? findSlideIdxById(lesson.slides, slideId)
            : 0;
        const safeIdx = Math.max(0, idx);
        return {
            sessionStarted: true,
            sessionEnded: false,
            slideSelected: true,
            slideIndex: safeIdx,
            fragmentIndex: 0,
            slideRenderMode: "full", // reveal every fragment at once
            currentSection: lesson.slides[safeIdx]?.section || "concept",
            aiPaused: false,
            blanked: false,
            mascotState: "idle",
            annotations: {},
            annotationTool: "off",
            annotationColor: "#2E8E9A",
            dragPlacements: {},
            ...stateOverrides,
        };
    }, [slideId, isGaseous, stateOverrides]);

    // Gaseous fallback: deck.js external HTML — no mobile preview.
    if (isGaseous) {
        const slide = gaseousLesson?.slides?.find((s) => s.id === slideId);
        return (
            <div
                style={{
                    width: "100vw",
                    height: "100vh",
                    background: "#F5FAFC",
                    color: "#14202a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 32,
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    textAlign: "center",
                }}
            >
                <div>
                    <div
                        style={{
                            fontSize: 11,
                            letterSpacing: 2,
                            textTransform: "uppercase",
                            color: "#8A95A3",
                            fontWeight: 700,
                            marginBottom: 8,
                        }}
                    >
                        External deck
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                        {slide?.title || "Loading…"}
                    </div>
                </div>
            </div>
        );
    }

    if (!previewState) {
        return (
            <div
                style={{
                    width: "100vw",
                    height: "100vh",
                    background: "#0A0F14",
                    color: "#8A95A3",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    fontSize: 14,
                }}
            >
                Slide not found
            </div>
        );
    }

    return (
        <div
            data-testid="slide-preview-root"
            data-slide-id={slideId || ""}
            data-photo-override={photoOverride ? "1" : ""}
            style={{
                width: "100vw",
                height: "100vh",
                position: "relative",
                overflow: "hidden",
                background: "#F5FAFC",
            }}
        >
            {photoOverride ? (
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        background: "#0d1a23",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    data-testid="slide-preview-photo"
                >
                    <img
                        src={photoOverride}
                        alt="Student work"
                        style={{
                            maxWidth: "94%",
                            maxHeight: "94%",
                            objectFit: "contain",
                            borderRadius: 12,
                            boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
                        }}
                    />
                </div>
            ) : (
                <LessonStage
                    state={previewState}
                    showChrome={false}
                    playAudio={false}
                    annotationProps={{ interactive: false }}
                    dragDropProps={{ interactive: false }}
                />
            )}
        </div>
    );
};

export default SlidePreview;
